/**
 * AnonymousQuotaCounter
 *
 * Quota display for anonymous users with exhausted/low/normal states.
 * Extracted from AnalysisSection.tsx (PR #242 audit).
 *
 * AUTH-UX-C (NocoDB #3908): Embeds anon→authed conversion CTAs.
 *  - Non-exhausted state: inline "Save your analyses" prompt with
 *    "Sign up free" primary + "Continue as guest" session-dismiss link.
 *  - Exhausted state: prepends a "Sign up to keep analyzing" CTA above
 *    the legacy Upgrade/View Plans buttons. Anonymous users have no
 *    subscription, so the signup CTA is the actionable next step; the
 *    legacy buttons stay for users who choose to explore tier pricing
 *    pre-signup.
 *
 * The CTA primary navigates to `/(auth)/signup?from=anon-cta&source=<id>`
 * so the signup screen can fire `Auth_AnonCTA_Clicked` on mount,
 * forming the source side of the conversion funnel that pairs with
 * `Auth_AnonAccountLinked` (sink, fired by AuthContext post-signup).
 */
import { View, Pressable } from "react-native";
import { useState } from "react";
import { useRouter } from "expo-router";
import { TierBody, TierHeadline, TierButton } from "@dramadetector/ui";
import { useTranslation } from "@dramadetector/shared";

interface AnonymousQuotaCounterProps {
  quotaRemaining: number;
  quotaLimit: number;
  quotaState: 'exhausted' | 'low' | 'normal';
  formattedResetTime: string;
  turnstileError: string | null;
  onUpgrade: () => void;
  onViewPlans: () => void;
}

export function AnonymousQuotaCounter({
  quotaRemaining,
  quotaLimit,
  quotaState,
  formattedResetTime,
  turnstileError,
  onUpgrade,
  onViewPlans,
}: AnonymousQuotaCounterProps) {
  const { t } = useTranslation('home');
  const router = useRouter();

  // Session-only dismiss for the non-exhausted CTA. The exhausted-state CTA
  // is intentionally non-dismissible — quota is at zero, signup is the only
  // forward path for an anon user.
  const [ctaDismissed, setCtaDismissed] = useState(false);

  const quotaContainerClass = {
    exhausted: 'bg-red-50 border-red-200',
    low: 'bg-orange-50 border-orange-200',
    normal: 'bg-blue-50 border-blue-200',
  }[quotaState];

  const goToSignup = (source: 'home-quota-counter' | 'quota-exhausted-counter') => {
    // Tag the navigation so the signup screen can attribute the funnel.
    // Auth_AnonCTA_Clicked is fired on signup-page mount (not click-time)
    // to count user-reaches-signup-page, which is the meaningful funnel
    // step that pairs with Auth_AnonAccountLinked.
    router.push(`/(auth)/signup?from=anon-cta&source=${source}`);
  };

  return (
    <View className="mb-4 max-w-md mx-auto w-full" testID="anonymous-quota-counter">
      <View className={`mb-3 px-3 py-3 rounded-lg border ${quotaContainerClass}`}>
        {quotaState === 'exhausted' ? (
          <View className="gap-2">
            <TierBody size="sm" className="text-red-700 text-center font-semibold">
              {t('home.trial_quota_exhausted', { remaining: 0, limit: quotaLimit })}
            </TierBody>
            <TierBody size="xs" className="text-red-600 text-center mt-1">
              {t('home.quota_resets', { resetTime: formattedResetTime })}
            </TierBody>

            {/* AUTH-UX-C: Signup CTA — primary action for anon at quota=0 */}
            <View
              className="mt-3 pt-3 border-t border-red-200 gap-2"
              testID="anon-cta-exhausted"
            >
              <TierHeadline as="h4" size="md" className="text-red-800 text-center">
                {t('home.anonCtaExhausted.headline')}
              </TierHeadline>
              <TierBody size="xs" className="text-red-700 text-center">
                {t('home.anonCtaExhausted.body', { limit: quotaLimit })}
              </TierBody>
              <View className="flex-row gap-2 mt-2 justify-center">
                <TierButton
                  variant="primary"
                  size="md"
                  onPress={() => goToSignup('quota-exhausted-counter')}
                  testID="anon-cta-exhausted-signup"
                >
                  {t('home.anonCta.primary')}
                </TierButton>
              </View>
            </View>

            <View className="flex-row gap-2 mt-3 justify-center flex-wrap">
              <TierButton variant="secondary" size="md" onPress={onUpgrade}>
                {t('home.upgrade_to_pro')}
              </TierButton>
              <TierButton variant="secondary" size="md" onPress={onViewPlans}>
                {t('home.view_plans')}
              </TierButton>
            </View>
          </View>
        ) : (
          <View>
            <TierBody
              size="xs"
              className={`text-center ${quotaState === 'low' ? 'text-orange-700' : 'text-blue-700'}`}
            >
              {t('home.trial_quota', { remaining: quotaRemaining, limit: quotaLimit })}
            </TierBody>
            {quotaState === 'low' && (
              <TierBody size="xs" className="text-orange-600 text-center mt-1">
                {t('home.quota_low_warning', { resetTime: formattedResetTime })}
              </TierBody>
            )}
            {quotaState === 'normal' && quotaRemaining < quotaLimit && (
              <TierBody size="xs" className="text-blue-600 text-center mt-1">
                {t('home.quota_resets', { resetTime: formattedResetTime })}
              </TierBody>
            )}
          </View>
        )}
      </View>

      {/* AUTH-UX-C: Inline conversion CTA for non-exhausted anon users.
          Hidden once dismissed for the session AND in the exhausted state
          (the exhausted branch above renders its own, more urgent CTA). */}
      {quotaState !== 'exhausted' && !ctaDismissed && (
        <View
          className="mb-3 px-3 py-3 rounded-lg border border-blue-100 bg-white gap-2"
          testID="anon-cta-inline"
        >
          <TierHeadline as="h4" size="md" className="text-gray-900 text-center">
            {t('home.anonCta.headline')}
          </TierHeadline>
          <TierBody size="xs" className="text-gray-600 text-center">
            {t('home.anonCta.body')}
          </TierBody>
          <View className="flex-row gap-2 mt-2 justify-center items-center flex-wrap">
            <TierButton
              variant="primary"
              size="md"
              onPress={() => goToSignup('home-quota-counter')}
              testID="anon-cta-inline-signup"
            >
              {t('home.anonCta.primary')}
            </TierButton>
            <Pressable
              onPress={() => setCtaDismissed(true)}
              accessibilityRole="button"
              accessibilityLabel={t('home.anonCta.dismiss')}
              testID="anon-cta-inline-dismiss"
              className="px-2 py-2"
            >
              <TierBody size="xs" className="text-gray-500 underline">
                {t('home.anonCta.dismiss')}
              </TierBody>
            </Pressable>
          </View>
        </View>
      )}

      {turnstileError && (
        <View className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <TierBody size="sm" className="text-red-800">
            {turnstileError}
          </TierBody>
        </View>
      )}
    </View>
  );
}
